import { useMemo, useEffect } from 'react';
import type { StepperStep } from '@tasama/ui';
import { useTranslation } from '@tasama/i18n';
import { useCompanyTypeFormData } from '../../features/company-type/stores/company-type-store';
import {
  useStepManagementStore,
  type StepManagementStore,
} from '../stores/step-management.store';
import {
  defaultStepRoutes,
  pifProjectStepRoutes,
  getDefaultSteps,
  getPifProjectSteps,
  stepPathMap,
} from '../config/step-config';

export function useOnboardingSteps(internalStep: string) {
  const { t } = useTranslation('onboarding');
  const companyTypeFormData = useCompanyTypeFormData();

  const { setCurrentStep, initializeSteps } = useStepManagementStore(
    (s: StepManagementStore) => s
  );
  const isStepCompleted = useStepManagementStore(
    (state: StepManagementStore) => state.isStepCompleted
  );
  // Subscribe to completedSteps so stepsWithCompletion re-renders when it changes
  const completedSteps = useStepManagementStore(
    (state: StepManagementStore) => state.completedSteps
  );

  const isPifProject = useMemo(() => {
    return (
      companyTypeFormData.companyType === 'pif-owned' &&
      companyTypeFormData.companyStatus === 'PIF_UNDER_DEVELOPMENT'
    );
  }, [companyTypeFormData.companyType, companyTypeFormData.companyStatus]);

  const stepRoutes = useMemo(() => {
    return isPifProject ? pifProjectStepRoutes : defaultStepRoutes;
  }, [isPifProject]);

  const steps: StepperStep[] = useMemo(() => {
    return isPifProject ? getPifProjectSteps(t) : getDefaultSteps(t);
  }, [isPifProject, t]);

  const stepLabels = useMemo(() => {
    return steps.map((step) => step.label);
  }, [steps]);

  useEffect(() => {
    initializeSteps(stepRoutes, stepLabels);
  }, [stepRoutes, stepLabels, initializeSteps]);

  useEffect(() => {
    const stepPath = stepPathMap[internalStep] || '/';
    setCurrentStep(stepPath);
  }, [internalStep, setCurrentStep]);

  const stepsWithCompletion: StepperStep[] = useMemo(() => {
    if (!steps || steps.length === 0) {
      return [];
    }

    return steps.map((step, index) => {
      const stepPath = stepPathMap[step?.id] || '/';
      const currentStepIndex = steps.findIndex((s) => s?.id === internalStep);
      return {
        ...step,
        completed: isStepCompleted(stepPath),
        current: index === currentStepIndex,
      };
    });
  }, [steps, completedSteps, isStepCompleted, internalStep]);

  const currentStepIndex = useMemo(() => {
    return steps.findIndex((step) => step.id === internalStep);
  }, [steps, internalStep]);

  const isConfirmationScreen = internalStep === 'confirmation';

  return {
    isPifProject,
    stepRoutes,
    steps,
    stepLabels,
    stepsWithCompletion,
    currentStepIndex,
    isConfirmationScreen,
  };
}
